import { ReactNode, createElement } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    IconNameTypes,
    Icon
} from "../../../../libraries/wpl/foundations/src/index";
import { Button, ButtonProps } from "../../button/src/";
import {
    Image,
    AspectRatios,
    PictureProps,
    Picture
} from "../../image/src/index";
import { Link, LinkProps } from "../../link/src/index";

export type HeaderTagTypes = "h1" | "h2" | "h3" | "h4" | "h5" | undefined;
export type OrientationTypes = "horizontal" | "vertical";

export interface ButtonAndLinkProps {
    buttonProps: ButtonProps;
    primaryLinkProps: LinkProps;
}

export interface ButtonAndSecondaryLinkProps {
    buttonProps: ButtonProps;
    secondaryLinkProps: LinkProps;
}

export interface OneLinkProps {
    primaryLinkProps?: LinkProps;
}

export interface OneButtonProps {
    buttonProps: ButtonProps;
}

export interface TwoLinksProps {
    primaryLinkProps: LinkProps;
    secondaryLinkProps: LinkProps;
}

export interface EyebrowProps {
    eyebrowText: string;
}

export type CTAProps =
    | OneButtonProps
    | OneLinkProps
    | ButtonAndLinkProps
    | ButtonAndSecondaryLinkProps
    | TwoLinksProps;

export interface ContentBlockProps {
    headerText: string | ReactNode;
    bodyText?: string | ReactNode;
    headerType?: HeaderTagTypes;
    footerHeader?: string;
    footerText?: string;
    /**
     * @deprecated - Please use pictureProps instead
     */
    imageLink?: string;
    /**
     * @deprecated - Please use pictureProps instead
     */
    imageAltText?: string;
    pictureProps?: PictureProps;
    decoration?: string;
    eyebrowText?: string;
    iconName?: IconNameTypes;
    iconColor?: string;
    className?: string;
    ctaProps?: CTAProps;
    isCentered?: boolean;
    cardStyle?: boolean;
    /**
     * @deprecated - Please use pictureProps instead
     */
    aspectRatio?: AspectRatios;
    orientation?: OrientationTypes;
    horizontalRule?: boolean;
}

function getHeaderTag(text: string | ReactNode, header: HeaderTagTypes) {
    const headerTag = header ? header : "h3";
    return createElement(headerTag, null, text);
}

/* Gives h tag one less than header */
function getEyebrowTag(
    headerType: HeaderTagTypes | undefined,
    eyebrowText: string | undefined
) {
    const tagNumber = headerType ? Number(headerType.slice(-1)) : 5;
    return createElement(
        `h${tagNumber + 1}`,
        { className: "contentBlock-eyebrow" },
        eyebrowText
    );
}

function renderLinks(ctaProps?: CTAProps) {
    if (!ctaProps) {
        return null;
    }
    let firstLink;
    if ("buttonProps" in ctaProps) {
        firstLink = <Button {...ctaProps.buttonProps} />;
    } else if ("primaryLinkProps" in ctaProps && ctaProps.primaryLinkProps) {
        firstLink = <Link {...ctaProps.primaryLinkProps} />;
    }
    const secondaryLink =
        "secondaryLinkProps" in ctaProps ? (
            <Link {...ctaProps.secondaryLinkProps} />
        ) : null;
    return (
        <div className="contentBlock-linkFlex">
            {firstLink}
            {secondaryLink}
        </div>
    );
}

/**
 * This method retrieves either the eyebrow or icon decoration. If both are present, the icon is returned.
 * @param headerType
 */
const getDecoration = (
    headerType?: HeaderTagTypes,
    eyebrowText?: string,
    iconName?: IconNameTypes,
    iconColor?: string
) => {
    if (iconName) {
        return <Icon name={iconName} color={iconColor} />;
    } else if (eyebrowText) {
        return getEyebrowTag(headerType, eyebrowText);
    }
    return null;
};

const getImage = (
    pictureProps?: PictureProps,
    imageLink?: string,
    imageAltText?: string,
    aspectRatio?: AspectRatios
) => {
    if (pictureProps) {
        return (
            <div className="img-section">
                <Picture {...pictureProps} />
            </div>
        );
    } else if (imageLink && imageAltText) {
        return (
            <div className="img-section">
                <Image
                    src={imageLink}
                    alt={imageAltText}
                    aspectRatio={aspectRatio}
                />
            </div>
        );
    } else {
        return null;
    }
};

// eslint-disable-next-line complexity
export const ContentBlock = ({
    headerText,
    bodyText,
    headerType = "h3",
    eyebrowText,
    className = "",
    imageLink,
    imageAltText,
    pictureProps,
    iconName,
    iconColor,
    ctaProps,
    aspectRatio = "1/1",
    orientation,
    cardStyle,
    isCentered = false,
    horizontalRule
}: ContentBlockProps) => {
    if (imageLink && !imageAltText) {
        throw new Error(
            "If using an image, please provide an alt text for accessibility purposes!"
        );
    }
    return (
        <div
            className={`content-block ${className}`}
            css={[
                isCentered &&
                    orientation === "horizontal" &&
                    cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%",
                                height: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",
                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation === "horizontal" &&
                    !cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%",
                                height: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",
                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation === "horizontal" &&
                    cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",
                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation === "horizontal" &&
                    !cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",

                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation === "horizontal" &&
                    cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation === "horizontal" &&
                    !cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%",
                                height: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation === "horizontal" &&
                    cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation === "horizontal" &&
                    !cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "grid",
                        "gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)",
                        "gap": "1rem",
                        [media.below.xs]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                            gap: "0"
                        },
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation !== "horizontal" &&
                    cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",
                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation !== "horizontal" &&
                    !cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation !== "horizontal" &&
                    cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",

                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation !== "horizontal" &&
                    !cardStyle &&
                    headerType === "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4",

                            fontSize: "1.25rem",
                            letterSpacing: "0.018rem",
                            fontFamily: "var(--bxp-charlie-display-font-family)"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                isCentered &&
                    orientation !== "horizontal" &&
                    cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "center",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "center",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation !== "horizontal" &&
                    cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            lineHeight: 0,
                            display: "block",
                            img: {
                                width: "100%"
                            }
                        },
                        ".text-section": {
                            "padding": "20px",
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    }),
                !isCentered &&
                    orientation !== "horizontal" &&
                    !cardStyle &&
                    headerType !== "h1" &&
                    css({
                        "display": "flex",
                        "flexDirection": "column",
                        ".img-section": {
                            "lineHeight": 0,
                            "display": "block",
                            "img": {
                                width: "100%"
                            },
                            "& + .text-section": {
                                marginBlockStart: "0.5rem"
                            }
                        },
                        ".text-section": {
                            "textAlign": "left",
                            "display": "flex",
                            "flexDirection": "column",
                            "& > *": {
                                margin: "0 0 24px"
                            }
                        },
                        "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "500",
                            lineHeight: "1.16"
                        },
                        "h1": {
                            fontSize: "3rem",
                            [media.only.sm]: {
                                fontSize: "2.75rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h2": {
                            fontSize: "2.75rem",
                            [media.only.sm]: {
                                fontSize: "2.5rem"
                            },
                            [media.below.sm]: {
                                fontSize: "2.25rem"
                            }
                        },
                        "h3": {
                            fontSize: "1.5rem"
                        },
                        "h4": {
                            fontSize: "1.25rem"
                        },
                        "p": {
                            lineHeight: "1.4"
                        },
                        "a, button": {
                            marginBlockStart: "0.5rem",
                            width: "fit-content",
                            fontWeight: "500"
                        },
                        ".contentBlock-body": {
                            fontWeight: "400"
                        },
                        ".contentBlock-eyebrow": {
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            letterSpacing: "0.1rem",
                            textTransform: "uppercase",
                            order: "-1"
                        },
                        ".icon": {
                            order: "-1"
                        },
                        ".contentBlock-linkFlex": {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10%",
                            justifyContent: "flex-start",
                            alignItems: "center"
                        }
                    })
            ]}
        >
            {/* Header is first in HTML for accessibility but order is changed visually through CSS */}
            {getImage(pictureProps, imageLink, imageAltText, aspectRatio)}

            <div className="text-section">
                {headerText && getHeaderTag(headerText, headerType)}

                {getDecoration(headerType, eyebrowText, iconName, iconColor)}

                {horizontalRule && (
                    <hr
                        css={css({
                            height: "2px",
                            maxWidth: "1200px"
                        })}
                    />
                )}

                {bodyText && <p className="contentBlock-body">{bodyText}</p>}

                {renderLinks(ctaProps)}
            </div>
        </div>
    );
};
